import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/apiClient';
import { fmtTs } from '../../lib/globalTz';

interface HuntRowDetail {
  priority: string;
  indicator: string;
  type: string;
  entity: string;
  events: string;
  firstSeen: string;
  lastSeen: string;
  sourceReport: string;
  extra: Record<string, string>;
}

interface HuntResult {
  n: number;
  table: string;
  scope: string;
  feed: string;
  hitCount: number;
  status: 'ALERT' | 'CLEAR' | 'ERROR';
  errorMessage?: string;
  rows: HuntRowDetail[];
}

interface ReportCard {
  id: string;
  title: string;
  date: string;
  url: string;
  source: string;
}

interface ThreatHuntingReportData {
  customerId: string;
  customerName: string;
  generatedAt: string;
  windowDays: number;
  reportsCollected: number;
  iocsRetrieved: number;
  newUniqueIocs: number;
  iocsIngestable: number;
  huntsCompleted: number;
  huntsWithHits: number;
  totalFindings: number;
  reportCards: ReportCard[];
  hunts: HuntResult[];
}

const PRIORITY_COLOR: Record<string, string> = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#0891b2',
};

const EXTRA_LABELS: Record<string, string> = {
  Processes: 'Process', ParentProcesses: 'Process', LoadingProcesses: 'Process', WritingProcesses: 'Process',
  CommandLines: 'Command line', ParentCmdLines: 'Command line', LoadingCmdLines: 'Command line', WritingCmdLines: 'Command line', CmdLines: 'Command line',
  FileNames: 'File', DecodedCmds: 'Decoded command',
  Accounts: 'Account', AccountUpns: 'Account (UPN)', UserList: 'Account', CallerList: 'Account', ActorList: 'Account',
  DeviceList: 'Device', TargetDevices: 'Device',
  RegistryKeys: 'Registry', ValueData: 'Registry value',
  RemoteIPs: 'Remote IP', SourceIPs: 'Remote IP',
  Ports: 'Port', DestPorts: 'Port',
  Domains: 'Domain', SiteUrls: 'URL', FileOriginUrls: 'File origin URL',
};

function Chip({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{title}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{subtitle}</div>
    </div>
  );
}

function StatCard({ value, label, color }: { value: number | string; label: string; color: string }) {
  return (
    <div style={{ flex: 1, minWidth: 140, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: '1.8em', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function InfoBox({ title, items }: { title: string; items: string[] }) {
  return (
    <div style={{ flex: 1, minWidth: 260, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 8 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
        {items.map((it) => <li key={it}>{it}</li>)}
      </ul>
    </div>
  );
}

type ReportPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

const PERIOD_TITLE: Record<ReportPeriod, string> = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly',
};

export function ThreatHuntingReportView({ customerId, customerLogoUrl, period = 'daily' }: { customerId: string; customerLogoUrl?: string; period?: ReportPeriod }) {
  const query = useQuery<ThreatHuntingReportData, Error>({
    queryKey: ['threat-hunting-report', customerId, period],
    queryFn: () => apiFetch<ThreatHuntingReportData>(
      `/threat-hunting-report?customerId=${encodeURIComponent(customerId)}&period=${period}`,
    ),
    enabled: !!customerId && customerId !== 'default',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (query.isLoading || query.isFetching) {
    return (
      <div className="rounded-2xl bg-white p-8 text-center text-slate-600 shadow-md">
        Running threat hunts against Sentinel, this can take up to a minute…
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="rounded-2xl border border-red-300 bg-red-50 p-6 text-sm text-red-700">
        {query.error.message}
      </div>
    );
  }

  const data = query.data;
  if (!data) return null;

  const alertingHunts = data.hunts.filter((h) => h.status === 'ALERT');
  const erroredHunts = data.hunts.filter((h) => h.status === 'ERROR');

  return (
    <div className="threat-hunting-report-root" style={{ background: '#f8fafc', borderRadius: 16, padding: 4, fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3c72 50%,#2a5298 100%)', padding: '24px 32px', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '10px 18px' }}>
              <img src="/kloudynet-logo.png?v=2" alt="Kloudynet" style={{ height: 36, display: 'block' }} />
            </div>
            <div style={{ flex: 1, textAlign: 'center', minWidth: 240 }}>
              <div style={{ fontSize: '1.6em', fontWeight: 300 }}>{PERIOD_TITLE[period]} Threat Intelligence Report</div>
              <div style={{ fontSize: '1em', opacity: 0.92, marginTop: 4 }}>{data.customerName}</div>
            </div>
            {customerLogoUrl ? (
              <div style={{ background: '#fff', borderRadius: 10, padding: '10px 18px' }}>
                <img src={customerLogoUrl} alt={data.customerName} style={{ height: 36, display: 'block' }} />
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
                TLP:AMBER
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '8px 24px', color: '#64748b', fontSize: 11.5, fontStyle: 'italic', borderBottom: '1px solid #e2e8f0' }}>
          Generated: {fmtTs(data.generatedAt, { dateStyle: 'full', timeStyle: 'short' })}
        </div>

        {/* Descriptor chip row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '14px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, alignSelf: 'flex-start' }}>TLP:AMBER</div>
          <Chip title={`${PERIOD_TITLE[period]} Threat Intelligence Briefing`} subtitle={`${data.windowDays}d ingestion · 7-day hunt window`} />
          <Chip title="Endpoint · Network · Identity · Email" subtitle="21 KQL hunts · 7-day retrospective" />
          <Chip title="Microsoft Sentinel" subtitle="Managed identity · STIX 2.1 · Kloudynet TI feed" />
        </div>

        <div style={{ padding: 24 }}>
          {/* Confirmed exposure banner */}
          {data.huntsWithHits > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>Confirmed exposure - action required</div>
              <div style={{ fontSize: 13, color: '#7f1d1d' }}>
                {data.huntsWithHits} of {data.hunts.length} hunts returned matches ({data.totalFindings} findings) when
                newly ingested indicators were correlated against historical telemetry. Alerting hunts:{' '}
                {alertingHunts.map((h) => `HUNT ${String(h.n).padStart(2, '0')}`).join(', ')}.
              </div>
            </div>
          )}

          {/* Executive summary */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ background: '#1e3c72', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>
              Executive Threat Summary
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 16, fontSize: 13.5, lineHeight: 1.6, color: '#334155' }}>
              During the reporting window ({data.windowDays === 1 ? 'the last 24 hours' : `the last ${data.windowDays} days`}),
              the Kloudynet Threat Intelligence platform retrieved <strong>{data.iocsRetrieved}</strong> raw
              Indicators of Compromise (IOCs) from external threat intelligence sources. Following deduplication,{' '}
              <strong>{data.newUniqueIocs}</strong> validated indicators of supported types (IPv4 addresses, domain names,
              file hashes, and URLs) were identified. Additionally, <strong>{data.reportsCollected}</strong> threat
              intelligence reports from the Kloudynet Threat Intelligence feed were processed. All 21 automated hunt
              queries were run against this customer's Sentinel workspace across endpoint, network, identity, email, and
              cloud telemetry.
            </div>
          </div>

          {/* Collection status row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
            <Chip title="Collection status" subtitle="Complete - all collection streams responded" />
            <Chip title={`Reports collected: ${data.reportsCollected}`} subtitle="Kloudynet Threat Intelligence feed" />
            <Chip title={`Indicators collected: ${data.iocsRetrieved}`} subtitle={`${data.newUniqueIocs} new to the workspace`} />
            <Chip title={`Hunt coverage: ${data.huntsCompleted} of ${data.hunts.length}`} subtitle={`${data.totalFindings} finding(s) across ${data.huntsWithHits} hunt(s)`} />
          </div>

          {/* Methodology + Indicator Coverage */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <InfoBox title="Methodology" items={[
              'Indicator extraction from verified threat intelligence providers',
              'Automated deduplication and classification prior to hunting',
              'STIX-aligned pattern generation for supported indicator types',
              `${data.hunts.length} automated threat hunt queries across endpoint, network, identity, email, and cloud telemetry`,
            ]} />
            <InfoBox title="Indicator Coverage" items={[
              'IPv4 Addresses - Malicious hosts and C2 infrastructure',
              'Domain Names - Adversary-controlled domains and phishing infrastructure',
              'URLs - Malicious endpoints, payload delivery and exfiltration paths',
              'File Hashes - SHA-256, SHA-1 and MD5 for malware and tooling',
              'Excluded Types - Email addresses, CVEs, ASNs, CIDRs (Sentinel unsupported)',
            ]} />
          </div>

          {/* Stat tiles */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatCard value={data.reportsCollected} label="Threat Reports" color="#1e3c72" />
            <StatCard value={data.iocsRetrieved} label="IOCs Retrieved" color="#0891b2" />
            <StatCard value={data.newUniqueIocs} label="New Unique IOCs" color="#7c3aed" />
            <StatCard value={data.iocsIngestable} label="IOCs Ingested to Sentinel" color="#d97706" />
            <StatCard value={data.huntsWithHits} label={`Hunt Queries with Hits (of ${data.hunts.length})`} color="#dc2626" />
          </div>

          {/* Threat Intelligence Report Feed */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ background: '#3b6bc4', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>
              Threat Intelligence Report Feed - {data.reportCards.length} reports
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 12, maxHeight: 420, overflowY: 'auto' }}>
              {data.reportCards.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No reports published in this window.</div>
              ) : (
                data.reportCards.map((r) => (
                  <div key={r.id || r.title} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a' }}>{r.title}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', margin: '4px 0 8px' }}>Published: {r.date} · {r.source}</div>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: '#3b6bc4', color: '#fff', fontSize: 11.5, fontWeight: 700, padding: '5px 12px', borderRadius: 6, textDecoration: 'none' }}>
                      View External Source
                    </a>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Live Threat Hunt Results table */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ background: '#3b6bc4', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>
              Live Threat Hunt Results - {data.hunts.length} KQL Queries
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {['Query', 'Table', 'Detection Scope', 'Feed', 'Result', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Result' || h === 'Status' ? 'center' : 'left', color: '#334155', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.hunts.map((h) => (
                    <tr key={h.n} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '7px 12px', fontWeight: 700, color: '#1e3c72' }}>HUNT {String(h.n).padStart(2, '0')}</td>
                      <td style={{ padding: '7px 12px', color: '#1e3c72' }}>{h.table}</td>
                      <td style={{ padding: '7px 12px', color: '#666' }}>{h.scope}</td>
                      <td style={{ padding: '7px 12px' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: h.feed === 'Standard TI' ? '#eff6ff' : h.feed === 'Deep Hunt' ? '#fef2f2' : '#f5f3ff', color: h.feed === 'Standard TI' ? '#1e3c72' : h.feed === 'Deep Hunt' ? '#dc2626' : '#7c3aed', fontWeight: 600 }}>
                          {h.feed}
                        </span>
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                        {h.status === 'ERROR' ? (
                          <span style={{ fontSize: 11, color: '#b45309' }}>ERR</span>
                        ) : h.hitCount > 0 ? (
                          <span style={{ background: 'rgba(255,45,85,.15)', color: '#dc3545', fontFamily: 'monospace', fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 700 }}>{h.hitCount} HITS</span>
                        ) : (
                          <span style={{ color: '#1e3c72', fontWeight: 700 }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                        {h.status === 'ALERT' ? (
                          <span style={{ background: '#dc3545', color: '#fff', fontSize: 10.5, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>ALERT</span>
                        ) : h.status === 'ERROR' ? (
                          <span style={{ background: '#f59e0b', color: '#fff', fontSize: 10.5, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>ERROR</span>
                        ) : (
                          <span style={{ background: '#f0fdf4', color: '#059669', fontSize: 10.5, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>CLEAR</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 16px', marginTop: 10, fontSize: 12.5, color: '#1e3c72', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span><strong>{data.huntsCompleted}</strong> of {data.hunts.length} queries completed</span>
              <span style={{ color: '#dc2626' }}><strong>{data.huntsWithHits}</strong> with hits</span>
              <span><strong>{data.totalFindings}</strong> total findings</span>
              <span><strong>{data.newUniqueIocs}</strong> indicators identified</span>
            </div>
          </div>

          {/* Errored hunts, so failures are diagnosable directly in the report */}
          {erroredHunts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ background: '#b45309', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>
                Hunt Errors ({erroredHunts.length})
              </div>
              <div style={{ border: '1px solid #fde68a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 14 }}>
                {erroredHunts.map((h) => (
                  <div key={h.n} style={{ marginBottom: 10, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>HUNT {String(h.n).padStart(2, '0')} - {h.table}</div>
                    <div style={{ color: '#92400e', fontFamily: 'monospace', fontSize: 11, marginTop: 2, wordBreak: 'break-word' }}>{h.errorMessage}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detection Detail */}
          {alertingHunts.length > 0 && (
            <div>
              <div style={{ background: '#dc2626', color: '#fff', padding: '10px 16px', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 13 }}>
                Detection Detail - {data.totalFindings} finding(s) across {alertingHunts.length} hunt(s)
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 16 }}>
                {alertingHunts.map((h) => (
                  <div key={h.n} style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 2 }}>
                      HUNT {String(h.n).padStart(2, '0')} - {h.table}
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#dc2626' }}>{h.hitCount} finding(s)</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 8 }}>{h.scope}</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {['Priority', 'Indicator', 'Type', 'Affected entity', 'Events', 'Activity window', 'Source report'].map((c) => (
                              <th key={c} style={{ padding: '6px 10px', textAlign: 'left', color: '#475569', fontWeight: 700, fontSize: 11 }}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {h.rows.map((row, i) => (
                            <>
                              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '6px 10px' }}>
                                  <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#fff', background: PRIORITY_COLOR[row.priority] ?? '#64748b', padding: '2px 8px', borderRadius: 8 }}>{row.priority}</span>
                                </td>
                                <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11.5, color: '#0f172a' }}>{row.indicator || '-'}</td>
                                <td style={{ padding: '6px 10px', color: '#334155' }}>{row.type || '-'}</td>
                                <td style={{ padding: '6px 10px', color: '#334155' }}>{row.entity || '-'}</td>
                                <td style={{ padding: '6px 10px', color: '#334155' }}>{row.events || '-'}</td>
                                <td style={{ padding: '6px 10px', color: '#64748b', fontSize: 11 }}>
                                  {row.firstSeen ? fmtTs(row.firstSeen, { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                                  {row.lastSeen && row.lastSeen !== row.firstSeen ? ` – ${fmtTs(row.lastSeen, { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                                </td>
                                <td style={{ padding: '6px 10px', color: '#334155', fontSize: 11 }}>{row.sourceReport || '-'}</td>
                              </tr>
                              {Object.keys(row.extra).length > 0 && (
                                <tr key={`${i}-extra`}>
                                  <td colSpan={7} style={{ padding: '8px 10px 12px', background: '#fafafa' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px 20px' }}>
                                      {Object.entries(row.extra).map(([k, v]) => (
                                        <div key={k} style={{ fontSize: 11 }}>
                                          <div style={{ color: '#94a3b8', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>{EXTRA_LABELS[k] ?? k}</div>
                                          <div style={{ color: '#475569', wordBreak: 'break-word' }}>{v}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', padding: '20px 0 4px', color: '#94a3b8', fontSize: 11 }}>
            Kloudynet Threat Intelligence · {data.customerName} · Confidential · TLP:AMBER
          </div>
        </div>
      </div>
    </div>
  );
}
